import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Box from '../atoms/Box';
import PromoBanner from '../molecules/PromoBanner';
import Apple24 from '../../icons/Apple24';
import GooglePlayColor24 from '../../icons/GooglePlayColor24';
import { detectAppPlatform, GTMAnalyticsSendDL } from '../../../utils/utils';
import APP_LINKS from '../../../utils/appLinks';

// `platform` = le store visé (format historique écouté par GTM côté Webflow, on n'y
// touche pas). `device` = la plateforme réellement détectée : sur desktop les deux
// badges sont affichés, sans ce champ tout clic desktop serait compté comme ios ou
// android et l'attribution des installs perdrait la distinction.
const trackStoreClick = (placement, store, device) => () => {
  GTMAnalyticsSendDL({
    event: store === 'ios' ? 'app_store_click' : 'play_store_click',
    placement,
    platform: store,
    device,
  });
};

// Badge sombre classique (App Store / Google Play), plus reconnaissable qu'un
// bouton texte brand — cf. retour design DIFF-A5.
const StoreBadge = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #000;
  color: #fff;
  border-radius: 8px;
  padding: 10px 16px;
  font-family: 'LTSaeada', Arial, sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid #d5ff4f;
    outline-offset: 2px;
  }
`;

/**
 * Encart de promo de l'app mobile (DIFF-A5). Réutilisé sur l'écran de
 * confirmation de réservation et dans l'espace compte, seule la copy change
 * selon `placement`. Même famille visuelle que le bloc "Simplifiez vos
 * prochains paiements" (PromoBanner).
 * @param {string} placement 'confirm' | 'compte' — clé i18n et jeton de campagne
 *
 * Les clés `appcard.${placement}.title/body` sont référencées dynamiquement :
 * un scanner statique de clés i18n ne les verra pas. Ne pas les supprimer
 * comme "inutilisées" (et de toute façon, `npm run translation` est banni
 * dans ce repo — voir CLAUDE.md).
 */
export default function AppPromoCard({ placement, ...props }) {
  const { t } = useTranslation();
  const [platform, setPlatform] = useState('desktop');
  const links = APP_LINKS[placement];

  useEffect(() => {
    setPlatform(detectAppPlatform());
  }, []);

  if (!links) {
    return null;
  }

  const showIOS = platform !== 'android';
  const showAndroid = platform !== 'ios';

  return (
    <PromoBanner
      icon="📱"
      title={t(`appcard.${placement}.title`)}
      description={t(`appcard.${placement}.body`)}
      tint="green"
      {...props}>
      <Box
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems="flex-start"
        style={{ gap: 8, rowGap: 8, columnGap: 12 }}>
        {showIOS && (
          <StoreBadge
            href={links.ios}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackStoreClick(placement, 'ios', platform)}>
            <Apple24 color="white" />
            {t('appcard.cta.ios')}
          </StoreBadge>
        )}
        {showAndroid && (
          <StoreBadge
            href={links.android}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackStoreClick(placement, 'android', platform)}>
            <GooglePlayColor24 />
            {t('appcard.cta.android')}
          </StoreBadge>
        )}
      </Box>
    </PromoBanner>
  );
}
